import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { useAuthStore } from '@/lib/stores/auth';
import { api } from '@/lib/api/client';
import { User, Mail, Shield, Calendar, Edit, Save, X } from 'lucide-react';
import { toast } from 'sonner';

interface UserProfile {
  id: number;
  email: string;
  name: string;
  role: string | { id: number; name: string };
  status: string;
  createdAt: string;
  lastLoginAt?: string;
  profilePictureUrl?: string | null;
}

export default function ProfilePage() {
  const { user, setUser } = useAuthStore();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setIsLoading(true);
    try {
      const response = await api.get<{ user: UserProfile } | UserProfile>('/api/users/me');
      if (response.success && response.data) {
        const userInfo = (response.data as { user: UserProfile }).user ?? (response.data as UserProfile);
        setProfile(userInfo);
        setEditName(userInfo.name ?? '');
      } else {
        setEditName(user?.name ?? '');
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      setEditName(user?.name ?? '');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!profile) {
      toast.error('Profile not loaded. Please refresh and try again.');
      return;
    }
    setIsSaving(true);
    try {
      const response = await api.put<{ user: UserProfile } | UserProfile>('/api/users/me', {
        name: editName
      });
      if (response.success && response.data) {
        const userInfo = (response.data as { user: UserProfile }).user ?? (response.data as UserProfile);
        setProfile(userInfo);
        setIsEditing(false);
        toast.success('Profile updated successfully');
      } else {
        toast.error(response.error?.message ?? 'Failed to update profile');
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditName(profile?.name || '');
    setIsEditing(false);
  };

  const handleAvatarSelected = async (file: File) => {
    if (!profile) return;
    setIsUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post<{ avatarUrl: string; user: UserProfile }>('/api/users/me/avatar', formData as any);

      if (response.success && response.data?.avatarUrl) {
        const avatarUrl = response.data.avatarUrl;
        setProfile((current) => current ? ({ ...current, profilePictureUrl: avatarUrl }) : current);
        if (user) {
          setUser({ ...user, profilePictureUrl: avatarUrl });
        }
        toast.success('Profile photo updated');
      } else {
        throw new Error(response.error?.message ?? 'Upload failed');
      }
    } catch (error: any) {
      console.error('Avatar upload failed:', error);
      toast.error(error?.message ?? 'Failed to upload profile photo');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const getRoleBadge = (role: string | { id: number; name: string } | unknown) => {
    const roleName = typeof role === 'string' ? role : (role as { name?: string })?.name || 'member';
    const variants: Record<string, 'default' | 'secondary' | 'outline'> = {
      'admin': 'default',
      'media_team': 'secondary',
      'moderator': 'outline',
      'pastor': 'outline',
      'member': 'outline'
    };
    return (
      <Badge variant={variants[roleName] || 'outline'}>
        {roleName.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="@container/profile max-w-[1800px] w-full mx-auto px-3 py-4 @[740px]/profile:px-4 @[740px]/profile:py-5 @[1100px]/profile:px-6 @[1100px]/profile:py-6 space-y-4 @[740px]/profile:space-y-5 @[1100px]/profile:space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold">Profile</h1>
      </div>

      {/* Profile Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-center gap-2">
              <Avatar className="h-20 w-20">
                <AvatarImage src={profile?.profilePictureUrl || undefined} />
                <AvatarFallback className="text-2xl">
                  {profile?.name?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <Label htmlFor="avatar-upload" className="sr-only">Upload profile photo</Label>
              <Input
                id="avatar-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleAvatarSelected(f);
                  e.currentTarget.value = '';
                }}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isUploadingAvatar}
                onClick={() => {
                  const input = document.getElementById('avatar-upload') as HTMLInputElement | null;
                  input?.click();
                }}
              >
                {isUploadingAvatar ? 'Uploading…' : 'Upload photo'}
              </Button>
            </div>
            <div className="flex-1">
              {isEditing ? (
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Your name"
                  />
                </div>
              ) : (
                <>
                  <CardTitle className="text-xl">{profile?.name || user?.name || 'Unknown User'}</CardTitle>
                  <CardDescription>{getRoleBadge(profile?.role || user?.role?.name || 'member')}</CardDescription>
                </>
              )}
            </div>
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <Button variant="outline" size="sm" onClick={handleCancel} disabled={isSaving}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? (
                      <LoadingSpinner size="sm" className="mr-2" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* User Info */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Email</p>
                <p className="text-muted-foreground">{profile?.email || user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Role</p>
                <p className="text-muted-foreground">{getRoleBadge(profile?.role || user?.role?.name || 'member')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Member Since</p>
                <p className="text-muted-foreground">
                  {profile?.createdAt
                    ? new Date(profile.createdAt).toLocaleDateString()
                    : user?.createdAt
                    ? new Date(user.createdAt).toLocaleDateString()
                    : 'Unknown'}
                </p>
              </div>
            </div>
            {profile?.lastLoginAt && (
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Last Login</p>
                  <p className="text-muted-foreground">
                    {new Date(profile.lastLoginAt).toLocaleString()}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
